import { useId } from "react";

import { Checkbox, ErrorMessage, LinkButton, Text } from "@vericus/cadmus-ui";

import { type AssessmentNoun } from "@/features/assignment";

import * as styles from "./agreement-styles.css";

export interface GenerativeAiAgreementProps {
  /**
   * Whether the student has agreed to the pledge.
   */
  isChecked?: boolean;

  /**
   * Canonical display term for this assessment in UI copy.
   */
  assessmentNoun?: AssessmentNoun;

  /**
   * Callback to update the checked value of the agreement.
   */
  onUpdateAgreement: (hasAgreed: boolean) => void;

  /**
   * Callback to open the additional GenAI instructions modal.
   * When provided, the "Additional Instructions" link is rendered.
   */
  onOpenAdditionalInstructions?: () => void;

  /**
   * Callback to open the University Guidelines manual preset.
   */
  onOpenUniversityGuidelines: () => void;
}

/**
 * Allow students to access and agree, or disagree, to the generative AI
 * pledge.
 */
export const GenerativeAiAgreement = ({
  isChecked,
  assessmentNoun = "assessment",
  onUpdateAgreement,
  onOpenAdditionalInstructions,
  onOpenUniversityGuidelines,
}: GenerativeAiAgreementProps) => {
  const pledgeId = useId();

  return (
    <div className={styles.container} data-testid="generative-ai-agreement">
      <div className={styles.agreementHeader}>
        <Text kind="label" textAlign="center">
          Generative AI Agreement
        </Text>
        <span className={styles.divider} />
      </div>
      <div
        className={
          isChecked !== true
            ? styles.agreementContainerWithError
            : styles.agreementContainer
        }
      >
        <Text kind="bodySm">
          The University acknowledges that Generative Artificial Intelligence
          (AI) tools are an important facet of contemporary life. Their use in
          assessments is considered in line with students&apos; development of
          the skills and knowledge which demonstrate the learning outcomes and
          underpin study and career success. Ensure that you adhere to the
          instructions for the use of Generative AI in this {assessmentNoun}. If
          in doubt, seek clarification.
        </Text>
        <Checkbox
          className={styles.agreementCheckbox}
          aria-labelledby={pledgeId}
          checked={isChecked}
          onChange={(e) => onUpdateAgreement(e.target.checked)}
        >
          <Text id={pledgeId} kind="bodySm" style={{ marginTop: 2 }}>
            {`I declare that in completing this ${assessmentNoun} I have adhered to the `}
            {onOpenAdditionalInstructions ? (
              <>
                <LinkButton
                  kind="primary"
                  inline
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenAdditionalInstructions();
                  }}
                >
                  Additional Instructions
                </LinkButton>
                {" and the "}
              </>
            ) : null}
            <LinkButton
              kind="primary"
              inline
              onClick={(e) => {
                e.stopPropagation();
                onOpenUniversityGuidelines();
              }}
            >
              University Guidelines
            </LinkButton>
            {` for the use of Generative AI outlined in this ${assessmentNoun}.`}
          </Text>
        </Checkbox>
      </div>
      {isChecked !== true && (
        <ErrorMessage fullWidth position="relative">
          You must review and accept the Generative AI Guidelines in order to
          submit
        </ErrorMessage>
      )}
    </div>
  );
};
